import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/Reminders.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function Reminders({ user }) {
  const [reminders, setReminders] = useState([]);
  const [form, setForm] = useState({ text: '' });
  const [editingId, setEditingId] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [statusType, setStatusType] = useState('success');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const roles = user?.roles || [];
  const canEdit = roles.includes('template_editor') || roles.includes('admin');

  useEffect(() => {
    fetchReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReminders = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/reminders/`);
      setReminders(res.data);
    } catch (err) {
      console.error('Error fetching reminders:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.text.trim()) return;
    try {
      if (editingId) {
        await axios.put(`${API_BASE_URL}/reminders/${editingId}/`, form);
        setStatusMsg('Reminder updated.');
      } else {
        await axios.post(`${API_BASE_URL}/reminders/`, form);
        setStatusMsg('Reminder created.');
      }
      setStatusType('success');
      setForm({ text: '' });
      setEditingId(null);
      fetchReminders();
    } catch (err) {
      const apiError = typeof err.response?.data === 'object' ? err.response?.data?.error : null;
      setStatusMsg('Error: ' + (apiError || (err.response?.status >= 500 ? 'Server error — please try again later.' : err.message)));
      setStatusType('error');
    }
  };

  const startEdit = (r) => {
    setForm({ text: r.text });
    setEditingId(r.id);
    setStatusMsg('');
  };

  const cancelEdit = () => {
    setForm({ text: '' });
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/reminders/${id}/`);
      setConfirmDelete(null);
      setStatusMsg('Reminder deleted.');
      setStatusType('success');
      fetchReminders();
    } catch (err) {
      setStatusMsg('Error deleting reminder.');
      setStatusType('error');
    }
  };

  if (!canEdit) return <div className="restricted">You do not have permission to access this page.</div>;

  return (
    <div className="reminders-page">
      <h2>Reminders</h2>
      <p className="reminders-intro">
        Short training-reminder messages. The Schedule page can pick one of these at
        random for each recipient when a scheduled job uses "Random Reminder".
      </p>

      {statusMsg && <div className={`status-msg status-${statusType}`}>{statusMsg}</div>}

      <div className="reminder-form-section">
        <h3>{editingId ? 'Edit Reminder' : 'New Reminder'}</h3>
        <form onSubmit={handleSubmit} className="reminder-form">
          <textarea
            placeholder="Reminder text — supports {{name}}, {{firstname}}, {{lastname}}, {{date}}"
            value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
            rows={3}
            required
          />
          <p className="placeholder-hint">
            Available placeholders: <code>{'{{name}}'}</code> <code>{'{{firstname}}'}</code> <code>{'{{lastname}}'}</code> <code>{'{{date}}'}</code>
          </p>
          <div className="form-actions">
            <button type="submit" className="btn-save">{editingId ? 'Update' : 'Create'}</button>
            {editingId && <button type="button" className="btn-cancel" onClick={cancelEdit}>Cancel</button>}
          </div>
        </form>
      </div>

      <div className="reminder-list-section">
        <h3>Existing Reminders ({reminders.length})</h3>
        {reminders.length === 0 ? (
          <p className="no-data">No reminders yet.</p>
        ) : (
          <ul className="reminder-list">
            {reminders.map((r) => (
              <li key={r.id} className="reminder-item">
                <div className="reminder-info">
                  <p className="reminder-text">{r.text}</p>
                </div>
                <div className="reminder-actions">
                  <button className="btn-edit" onClick={() => startEdit(r)}>Edit</button>
                  {confirmDelete === r.id ? (
                    <>
                      <span className="confirm-text">Delete?</span>
                      <button className="btn-confirm-delete" onClick={() => handleDelete(r.id)}>Yes</button>
                      <button className="btn-cancel" onClick={() => setConfirmDelete(null)}>No</button>
                    </>
                  ) : (
                    <button className="btn-delete" onClick={() => setConfirmDelete(r.id)}>Delete</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default Reminders;
